
export enum SupportedLanguage {
  EN = 'en',
  IT = 'it',
  ES = 'es',
  FR = 'fr',
  PT = 'pt',
  DE = 'de',
  PL = 'pl',
  NL = 'nl',
  RU = 'ru'
}

export type ActivityType = 'workout' | 'diet' | 'steps' | 'cardio' | 'yoga' | 'gym' | 'general';

export type GoalCategory = 'fitness' | 'diet' | 'social';
export type GoalStatus = 'active' | 'completed' | 'abandoned' | 'failed';
export type GoalHealth = 'ON_TRACK' | 'BEHIND' | 'AT_RISK';

export type NotificationType = 'motivational' | 'social' | 'challenge' | 'system' | 'ai_insight';

export interface SavedPlan {
  id: string;
  userId: string;
  type: 'workout' | 'diet';
  title: string;
  content: any; // Struttura JSON generata dall'AI
  createdAt: any;
}

export interface AIAnalysis {
  bodyFatPercentage: string;
  muscleDefinition: number; // 1-10
  posture: string;
  visibleMuscles: string[];
  suggestions: string[];
  encouragement: string;
  processedAt: any;
}

export interface ProgressPhoto {
  id: string;
  userId: string;
  imageUrl: string;
  uploadedAt: any;
  weight?: number;
  notes?: string;
  privacy: 'private' | 'friends' | 'public';
  aiAnalysis?: AIAnalysis;
  measurements?: {
    chest?: number;
    waist?: number;
    hips?: number;
    arms?: number;
    legs?: number;
  };
}

export interface Challenge {
  id: string;
  title: string;
  description: string;
  difficulty: 'easy' | 'medium' | 'hard';
  durationDays: number;
  minLevel: number;
  targetValue: number;
  category: GoalCategory;
  xpReward: number;
  type?: 'fitness' | 'diet' | 'social';
  status?: 'active' | 'completed' | 'failed';
  currentValue?: number;
}

export interface NotificationSettings {
  categories: {
    motivational: boolean;
    social: boolean;
    challenge: boolean;
    system: boolean;
    ai_insight: boolean;
  };
  doNotDisturb: {
    start: string;
    end: string;
  };
  maxPerDay: number;
}

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  isRead: boolean;
  actionUrl?: string;
  createdAt: any;
}

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  type: NotificationType;
  read: boolean;
  createdAt: number;
}

export interface Goal {
  id: string;
  userId: string;
  title: string;
  description: string;
  category: GoalCategory;
  targetValue: number;
  currentValue: number;
  unit: string;
  deadline?: any;
  status: GoalStatus;
  health: GoalHealth;
  createdAt: any;
  updatedAt: any;
  progressHistory: { date: string; value: number }[];
  completedAt?: any;
  xpReward: number;
}

export interface Message {
  id?: string;
  role: 'user' | 'model';
  text: string;
  timestamp?: any;
  groundingChunks?: any[];
}

export interface ChatMessage {
  id?: string;
  senderId: string;
  text: string;
  imageUrl?: string; // Optional, future-proof for image messages
  createdAt: any; // Firestore serverTimestamp
  readBy: { [uid: string]: boolean }; // Using map format for efficient Firestore queries
  // NOTE: Using map format instead of array for better Firestore rule performance
  // TODO: If array format is required, consider migration helper function
  type: 'text'; // Extensible for future message types
}

export interface Conversation {
  id?: string;
  type: 'private' | 'group' | 'direct'; // 'direct' kept for backward compatibility, prefer 'private'
  participants: string[]; // For direct: exactly 2 uids; for group: >=2 uids
  // NOTE: Using 'participants' instead of 'members' for backward compatibility with existing data
  // TODO: Consider migration to 'members' in future if needed, but current implementation works with 'participants'
  participantsMap: { [uid: string]: true }; // For Firestore rules
  createdAt: any; // Firestore serverTimestamp
  updatedAt?: any; // Firestore serverTimestamp - ✅ Required for inbox sorting (optional for backward compatibility)
  lastMessage: string;
  lastMessageAt: any; // Firestore serverTimestamp
  lastSenderId: string;
  unreadCount: { [uid: string]: number }; // Per participant unread count
  // Group-specific fields (only present when type === 'group')
  title?: string; // Group name
  description?: string;
  photoURL?: string; // Optional group avatar URL
  createdBy?: string; // Creator uid
  adminsMap?: { [uid: string]: true }; // Admin uids (only for groups)
}

export interface User {
  id: string;
  email: string;
  name: string;
  username?: string; // Unique username (can be changed once every 30 days)
  avatar: string;
  photoURL?: string; // Profile picture URL (synced with avatar for consistency)
  level: number;
  xp: number;
  streak?: number; // DEPRECATED: Kept for backward compatibility, use calendar instead
  lastActiveDate?: string; // DEPRECATED: Kept for backward compatibility
  role: 'user' | 'trainer' | 'admin';
  plan: 'free' | 'pro' | 'unlimited';
  planStatus?: 'active' | 'expired' | 'trial';
  billingPeriod?: 'monthly' | 'yearly'; // Optional for backward compatibility - defaults to 'monthly' if not set
  hasAcceptedTerms?: boolean;
  hasOnboarded?: boolean;
  notificationSettings?: NotificationSettings;
  badges: string[];
  activeChallenges: string[];
  title?: string;
  age?: number;
  weight?: number;
  height?: number;
  gender?: 'male' | 'female';
  allergies?: string;
  following?: string[];
  referralCode?: string;
  referredBy?: string;
  referralStats?: ReferralStats;
  referralSubscriptions?: string[]; // Array of user IDs that have generated a referral reward (prevents duplicates)
  referralRewards?: {
    earned: number; // Number of referrals that earned XP
    pending: number; // Number of converted referrals waiting for XP
  };
  referralBonusUsed?: boolean; // Legacy field - use referralStats.referralBonusUsed instead
  wearable?: WearableData;
  goals?: {
    steps: number;
  };
  friends?: string[];
  feedbackXPVersions?: string[]; // Array of app versions for which user has already received feedback XP (anti-abuse)
  bio?: string; // User bio/description for social profile
  usernameLastChanged?: any; // Timestamp of last username change (for 30-day limit)
  provider?: 'password' | 'google' | 'facebook' | 'apple'; // Authentication provider
  createdAt?: any; // User account creation timestamp
  lastLoginAt?: any; // Last login timestamp

  // ✅ Added for GamificationComponents.tsx usage
  public?: boolean;
  private?: boolean;

  // ✅ NEW: Core Repositioning & Discipline System
  mainObjective?: 'strength' | 'endurance' | 'weight_loss' | 'muscle_gain' | 'flexibility' | 'general_health';
  promiseDays?: 7 | 14 | 30; // User-selected promise duration
  promiseStartDate?: string; // ISO date string
  ritualCompleted?: boolean; // 7-day ritual completion status
  ritualStartDate?: string; // ISO date string
  ritualCurrentDay?: number; // Current day in ritual (1-7)
  dailyCoreActionCompleted?: boolean; // Today's core action status
  dailyCoreActionDate?: string; // Last date core action was completed
  dailyCoreActionType?: 'workout_confirm' | 'meal_mark' | 'reflection' | 'custom'; // Type of core action
  dailyCoreActionText?: string; // For reflection type, stores the text
  consecutiveCoreActions?: number; // DEPRECATED: Use calendar stamps instead
  
  // ✅ NEW: Monthly Calendar Stamp System (replaces streak)
  monthlyCalendars?: MonthlyCalendar[]; // Array of monthly calendars (current + archived)
  currentCalendarMonth?: string; // Format: "YYYY-MM" for current active month
  calendarConfig?: CalendarConfig; // User-specific config (can override defaults)
}

export type View =
  | 'feed'
  | 'coach'
  | 'map'
  | 'shop'
  | 'dashboard'
  | 'referral'
  | 'admin'
  | 'wearable'
  | 'plans'
  | 'reports'
  | 'goals'
  | 'notifications'
  | 'settings'
  | 'progress'
  | 'privacy'
  | 'terms'
  | 'refund'
  | 'contact'
  | 'messages'
  | 'chat';

export type FeedbackType = 'bug' | 'suggestion' | 'general';
export type FeedbackStatus = 'new' | 'reviewed' | 'resolved' | 'closed';

export interface Feedback {
  id?: string;
  userId: string;
  userName: string;
  userEmail: string;
  type: FeedbackType;
  rating: number; // 1-5
  message: string; // Max 500 characters
  screenshotUrl?: string;
  metadata: {
    appVersion: string;
    userAgent: string;
    platform: string;
    view?: string; // Current view when feedback was submitted
    plan?: string;
    language?: string;
  };
  status: FeedbackStatus;
  xpAwarded?: boolean; // Whether XP was awarded for this feedback
  createdAt: any;
  reviewedAt?: any;
  resolvedAt?: any;
  adminNotes?: string;
}

// Social Feed Post Interface
export interface Post {
  id: string;
  userId: string; // authorId (embedded for denormalization)
  userName: string; // authorName (embedded for denormalization)
  userAvatar: string; // authorAvatar (embedded for denormalization)
  userLevel?: number; // authorLevel (embedded for denormalization, optional for backward compatibility)
  text: string;
  imageUrl?: string; // URL to uploaded image
  videoUrl?: string; // URL to uploaded video
  mediaType?: 'image' | 'video'; // Type of media content
  likes: number;
  commentsCount: number;
  timestamp: any;
  type: 'workout' | 'meal' | 'achievement' | 'general';
  privacy?: 'public' | 'friends' | 'private'; // For future moderation/visibility features
  status?: 'active' | 'reported' | 'hidden'; // For future moderation
  public?: boolean; // Explicit public flag for feed filtering
}

export interface ReferralStats {
  totalInvites: number;
  confirmedSubscriptions: number;
  currentDiscountPercent: number;
  totalXPEarned: number;
  discountsUsed: number;
  referralBonusUsed?: boolean; // True when 100% discount has been used, resets counters
  referralDiscountLocked?: boolean; // True when discount is at 100% and accumulation is paused
}

export type ReferralStatus = 'pending' | 'converted' | 'rewarded';

export interface Referral {
  id?: string;
  referrerId: string;
  referredUserId: string;
  status: ReferralStatus;
  createdAt: any;
  convertedAt?: any;
  rewardedAt?: any;
  planType?: 'pro' | 'unlimited'; // Plan type when converted
}

export interface Product {
  id: string;
  name: string;
  price: number;
  category: 'clothing' | 'equipment' | 'supplements';
  image: string;
  isReferralEligible?: boolean;
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'fitness' | 'streak' | 'social' | 'diet';
}

export interface WeeklyStats {
  weekId: string;
  daysActive: number;
  totalXP: number;
  xpDiffPercent: number;
  workouts: number;
  dietDays: number;
  streak: number;
  badgesUnlocked: string[];
  rankChange: number;
  newFollowers: number;
  topActivity: ActivityType;
}

export interface GoalTemplate {
  id: string;
  title: string;
  description: string;
  category: GoalCategory;
  targetValue: number;
  unit: string;
  xpReward: number;
  defaultDeadlineDays?: number;
}

export interface TrackingLog {
  id?: string;
  userId: string;
  type: ActivityType;
  timestamp: any;
  xpEarned: number;
  isManual: boolean;
  metadata?: any;
}

export interface WearableData {
  deviceType?: 'apple' | 'android' | 'fitbit' | 'garmin' | 'other';
  lastSync?: any;
  steps?: number;
  heartRate?: number;
  calories?: number;
  caloriesBurned?: number;
  sleepHours?: number;
  provider?: string;
  activeMinutes?: number;
  distance?: number;
}

// ✅ NEW: Monthly Calendar Stamp System (replaces streak)
export type StampType = 'standard' | 'special' | 'bonus';
export type RewardType = 'xp_boost' | 'discount' | 'cosmetic' | 'unlockable';

export interface DailyStamp {
  date: string; // ISO date string (YYYY-MM-DD)
  type: StampType;
  xpEarned: number;
  rewardUnlocked?: RewardType;
  rewardUsed?: boolean;
  reflection?: string; // Optional user reflection
  bonus?: {
    type: string;
    value: number;
  };
}

export interface MonthlyCalendar {
  year: number;
  month: number; // 1-12
  stamps: DailyStamp[];
  totalStamps: number;
  milestonesReached: number[]; // Array of milestone day numbers (e.g., [5, 10, 20])
  rewardsEarned: {
    type: RewardType;
    value: any;
    unlockedAt: string; // ISO date
    used: boolean;
    expiresAt?: string; // ISO date for discounts
  }[];
  archived: boolean; // True for past months
}

export interface CalendarConfig {
  xpPerStamp: {
    standard: number;
    special: number;
    bonus: number;
  };
  milestones: {
    day: number;
    reward: {
      type: RewardType;
      value: any;
    };
  }[];
  bonusChance: number; // 0-1 probability of bonus stamp
}
